import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { User } from "@/types/auth";
import {
  canSkipOnboardingGuard,
  computeNextOnboardingStep,
  getNextOnboardingStep,
  isOnboardingFlowPath,
  isProfileComplete,
} from "@/features/onboarding/onboardingGuard";
import { useOnboardingStatus } from "@/features/onboarding/useOnboardingStatus";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function getOnboardingFallbackPath(user: User) {
  if (!isProfileComplete(user)) {
    return "/confirm";
  }
  return "/subscribe";
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, user, isLoading, logout } = useAuth();
  const location = useLocation();
  const skipGuard = canSkipOnboardingGuard(location.pathname);

  // Only drive the (deduped, cached) status query when authenticated
  const guardUser = !isLoading && isAuthenticated && user ? user : null;
  const statusQuery = useOnboardingStatus(guardUser);

  if (isLoading) {
    return <RouteSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated but the user record is still loading: hold a spinner rather
  // than rendering page content we may immediately redirect away from.
  if (!user) {
    return <RouteSpinner />;
  }

  // Deleted user: fire a full logout so isAuthenticated becomes false before
  // /login renders, preventing PublicRoute from looping back into a status check.
  const userNotFound = statusQuery.data?.userFound === false;
  React.useEffect(() => {
    if (userNotFound) {
      logout(true).catch(() => {/* ignore */});
    }
  }, [userNotFound, logout]);

  if (userNotFound) {
    return <RouteSpinner />;
  }

  if (skipGuard) {
    return <>{children}</>;
  }

  // The onboarding decision needs the status snapshot. Until it is known we
  // show a single spinner and never paint an intermediate page.
  if (statusQuery.data === undefined) {
    if (statusQuery.isError) {
      const fallback = getOnboardingFallbackPath(user);
      return location.pathname === fallback ? (
        <>{children}</>
      ) : (
        <Navigate to={fallback} replace />
      );
    }
    return <RouteSpinner />;
  }

  // When the user arrived at a broker page from /strategies they are adding a
  // new strategy/broker — not going through first-time onboarding. Skip the
  // guard redirect so the broker connection page renders normally.
  const fromStrategies =
    (location.state as { from?: string } | null)?.from === "/strategies";
  if (fromStrategies && isOnboardingFlowPath(location.pathname)) {
    return <>{children}</>;
  }

  const nextStep = computeNextOnboardingStep(
    user,
    location.pathname,
    statusQuery.data
  );

  if (nextStep) {
    return <Navigate to={nextStep} replace />;
  }

  return <>{children}</>;
};

interface PublicRouteProps {
  children: React.ReactNode;
}

export const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();
  const [nextStep, setNextStep] = React.useState<string | null>(null);
  const [isChecking, setIsChecking] = React.useState(false);

  React.useEffect(() => {
    const check = async () => {
      if (isLoading) return;
      if (isAuthenticated && user && location.pathname === "/login") {
        if (!isProfileComplete(user)) {
          setNextStep(null);
          setIsChecking(false);
          return;
        }
        setIsChecking(true);
        try {
          const destination = await getNextOnboardingStep(user, "/dashboard");
          setNextStep(destination || "/dashboard");
        } catch {
          setNextStep(getOnboardingFallbackPath(user));
        } finally {
          setIsChecking(false);
        }
      } else {
        setNextStep(null);
        setIsChecking(false);
      }
    };
    check();
  }, [isAuthenticated, user, location.pathname, isLoading]);

  if (isLoading || isChecking) {
    return <RouteSpinner />;
  }

  if (isAuthenticated && user && location.pathname === "/login" && nextStep) {
    return <Navigate to={nextStep} replace />;
  }

  return <>{children}</>;
};

function RouteSpinner() {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center bg-black">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-white" />
    </div>
  );
}
